import { minBy } from 'es-toolkit';

import Direction2D from './Direction2D';
import Direction3D from './Direction3D';
import Isometry from './Isometry';
import Line2D from './Line2D';
import Line3D from './Line3D';
import LineSegment2D from './LineSegment2D';
import LineSegment3D from './LineSegment3D';
import Point2D from './Point2D';
import Point3D from './Point3D';
import Polygon2D from './Polygon2D';
import Polygon3D from './Polygon3D';

/**
 * A representation of the position and orientation of a local 2D coordinate system within 3D space. The embedding is
 * isometric, meaning that distances, angles, etc. are preserved when mapping from the 2D space to the 3D space. There
 * is no scaling/skewing.
 */
class IsometricEmbedding {
  /**
   * The direction in 3D space of the positive direction of the 2D x-axis.
   */
  public readonly xAxis: Direction3D;

  /**
   * The direction in 3D space of the positive direction of the 2D y-axis.
   */
  public readonly yAxis: Direction3D;

  /**
   * The position in 3D space of the origin of the 2D space.
   */
  public readonly origin: Point3D;

  constructor(xAxis: Direction3D, yAxis: Direction3D, origin: Point3D) {
    this.xAxis = xAxis;
    this.yAxis = yAxis;
    this.origin = origin;
  }

  public static fromPointAndNormalDirection(point: Point3D, normalDirection: Direction3D) {
    const standardAxes = [Direction3D.right, Direction3D.up, Direction3D.out];
    const nonParallelStandardAxis = minBy(standardAxes, (axis) => Math.abs(axis.dotProduct(normalDirection)))!;

    const planarAxis1 = normalDirection.crossProductDirection(nonParallelStandardAxis);
    if (!planarAxis1) throw new Error('Expected cross product to exist.');

    const planarAxis2 = normalDirection.crossProductDirection(planarAxis1);
    if (!planarAxis2) throw new Error('Expected cross product to exist.');

    const origin = point.projectOntoDirection(normalDirection);

    return new IsometricEmbedding(planarAxis1, planarAxis2, origin);
  }

  public pointTo2D(point: Point3D) {
    const mappedPoint = new Point2D(
      point.subtract(this.origin).dotProduct(this.xAxis.toPoint()),
      point.subtract(this.origin).dotProduct(this.yAxis.toPoint()),
    );

    if (!point.equals(this.pointTo3D(mappedPoint)))
      throw new Error('The point does not lie within the embedded space.');

    return mappedPoint;
  }

  public pointTo3D(point: Point2D) {
    return this.origin.add(this.xAxis.toPoint(point.x)).add(this.yAxis.toPoint(point.y));
  }

  public segmentTo2D(segment: LineSegment3D) {
    return new LineSegment2D(this.pointTo2D(segment.basePoint), this.pointTo2D(segment.endPoint));
  }

  public segmentTo3D(segment: LineSegment2D) {
    return new LineSegment3D(this.pointTo3D(segment.basePoint), this.pointTo3D(segment.endPoint));
  }

  public directionTo2D(direction: Direction3D) {
    const result = new Direction2D(direction.dotProduct(this.xAxis), direction.dotProduct(this.yAxis));
    if (!direction.equals(this.directionTo3D(result)))
      throw new Error('The direction does not lie within the embedded space.');
    return result;
  }

  public directionTo3D(direction: Direction2D) {
    return Direction3D.fromObject(this.xAxis.toVector(direction.x).add(this.yAxis.toVector(direction.y)))!;
  }

  lineTo2D(line: Line3D) {
    const basePoint = this.pointTo2D(line.pointNearestOrigin);
    const { direction } = this.pointTo2D(line.direction.toPoint().add(line.pointNearestOrigin)).subtract(
      basePoint,
    ).vectorFromOrigin;
    return new Line2D(basePoint, direction);
  }

  public lineTo3D(line: Line2D) {
    const basePoint = this.pointTo3D(line.basePoint);
    const direction = this.directionTo3D(line.direction);
    return new Line3D(direction, basePoint);
  }

  polygonTo2D(polygon: Polygon3D) {
    return new Polygon2D(polygon.vertices.map(this.pointTo2D.bind(this)));
  }

  polygonTo2DWithoutOrientationValidation(polygon: Polygon3D) {
    return new Polygon2D(polygon.vertices.map(this.pointTo2D.bind(this)), false);
  }

  polygonTo3D(polygon: Polygon2D) {
    return new Polygon3D(polygon.vertices.map(this.pointTo3D.bind(this)), { embedding: this, source: polygon });
  }

  /**
   * Returns an isometry which contains this embedding as a subset. As is usual when increasing dimensionality, some
   * conventions are followed; namely, that _x_ and _y_ axes in the embedding correspond to _x_ and _y_ axes in the
   * isometry, respectively; and that the isometry is right-handed.
   */
  toIsometry() {
    return new Isometry(this.xAxis, this.yAxis, this.xAxis.crossProductDirection(this.yAxis)!, this.origin);
  }
}

export default IsometricEmbedding;
